import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AuthService } from 'src/app/services/auth.service';
import { formatCurrency } from 'src/app/services/constants';
import {
  normalizeCreditActions,
  resolveCreditBounds,
  VesselFinderCreditAction,
} from 'src/app/services/operators/vesselfinder-credit-actions';

/**
 * "What is a credit?", for VesselFinder — a deliberate duplicate of
 * `VoyageCreditInfoComponent` (same reasoning as
 * `VesselFinderCreditPurchaseComponent`'s header: parameterizing the live
 * Voyage Radar dialog was judged riskier than a second small, read-only
 * component for this pass). Reuses the same stylesheet and the same shared
 * `app-voyage-credit-catalogue` list.
 *
 * No `target`/AOI-POI concept for this operator in this phase — the
 * catalogue carries no `target` field at all, so there is no "some rows are
 * area-only" badge to show; `showTargets` is simply never bound.
 */
@Component({
  selector: 'app-vesselfinder-credit-info',
  templateUrl: './vesselfinder-credit-info.component.html',
  styleUrls: ['../voyage-credit-info/voyage-credit-info.component.scss'],
})
export class VesselFinderCreditInfoComponent implements OnInit {
  actions: VesselFinderCreditAction[] = [];
  unitPrice = 0;
  showPricing = false;
  minCredits = 0;
  maxCredits: number | null = null;

  private activeUser: any;

  constructor(
    private dialogRef: MatDialogRef<VesselFinderCreditInfoComponent>,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.authService.user.subscribe((user) => (this.activeUser = user));

    const row = this.data?.selectedRow;
    this.actions = normalizeCreditActions(row?.apiCreditActions);
    this.unitPrice = Number(row?.unitcost ?? row?.latency?.[0]?.unitcost ?? 0);
    this.showPricing = this.unitPrice > 0;

    const bounds = resolveCreditBounds(row, this.actions);
    this.minCredits = bounds.min;
    this.maxCredits = bounds.max;
  }

  /** Same obfuscation-aware fallback as vesselfinder.operator-strategy.ts. */
  get sensorName(): string {
    return this.data?.selectedRow?.displayName ?? 'This sensor';
  }

  get boundsLabel(): string {
    return this.maxCredits === null
      ? `${this.formatCredits(this.minCredits)} credits or more, per task.`
      : `${this.formatCredits(this.minCredits)} – ${this.formatCredits(this.maxCredits)} credits, per task.`;
  }

  formatCredits(n: number | null): string {
    return Number(n || 0).toLocaleString('en-US');
  }

  costLabel(cost: number): string {
    if (this.activeUser?.pointsEligible && this.activeUser?.sensorConversionFactor) {
      const [part1, part2] = this.activeUser.sensorConversionFactor.split(':').map(parseFloat);
      const conversionFactor = part2 / part1;
      return `${Math.round(cost * conversionFactor)} Points`;
    }
    return formatCurrency(cost);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
